import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import type { VariableResponse } from "../../lib/variableCascade";
import { unicodeCaseFold } from "../../lib/unicodeCaseFold";
import {
  Button,
  Inline,
  InlineError,
  Popover,
  Select,
  Skeleton,
  Stack,
  Text,
} from "../../ui";

export type InsertableVariable = Pick<
  VariableResponse,
  "id" | "key" | "scope_type"
> & { scopeLabel?: string };

export interface InsertVariableMenuProps {
  disabled?: boolean;
  emptyMessage?: string;
  emptyAction?: ReactNode;
  error?: boolean;
  isPending?: boolean;
  label?: string;
  onInsert: (variable: InsertableVariable) => void;
  onRetry: () => void;
  description?: string;
  variables: readonly InsertableVariable[];
}

/** One shared, request-free picker; the owner supplies its permitted declarations. */
export function InsertVariableMenu({
  disabled = false,
  description,
  emptyMessage = "No variables available. Declare a variable to insert it.",
  emptyAction,
  error = false,
  isPending = false,
  label = "Insert variable",
  onInsert,
  onRetry,
  variables,
}: InsertVariableMenuProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const selected = variables.find((variable) => variable.id === selectedId);
  useEffect(() => {
    if (isOpen) {
      document
        .getElementById(id)
        ?.querySelector<HTMLElement>('[role="combobox"], button')
        ?.focus();
    }
  }, [isOpen, id, isPending, error]);
  return (
    <Inline gap={2}>
      <Button
        aria-controls={isOpen ? id : undefined}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        disabled={disabled}
        ref={anchorRef}
        variant="secondary"
        onClick={() => {
          setSelectedId("");
          setIsOpen((current) => !current);
        }}
      >
        {label}
      </Button>
      <Popover
        anchorRef={anchorRef}
        aria-label={label}
        id={id}
        isOpen={isOpen && !disabled}
        onDismiss={() => {
          setIsOpen(false);
          anchorRef.current?.focus();
        }}
        returnFocusOnDismiss={false}
      >
        <Stack gap={3}>
          {isPending ? (
            <Skeleton label="Loading variables" shape="detail-field" />
          ) : error ? (
            <>
              <InlineError>Variables could not be loaded.</InlineError>
              <Button variant="secondary" onClick={onRetry}>
                Retry
              </Button>
            </>
          ) : variables.length === 0 ? (
            <>
              <Text variant="muted">{emptyMessage}</Text>
              {emptyAction}
            </>
          ) : (
            <>
              <Select
                description={description}
                label="Variable"
                value={selected?.id ?? ""}
                onChange={(event) => {
                  setSelectedId(event.currentTarget.value);
                }}
              >
                <option value="">Select variable</option>
                {variables.map((variable) => (
                  <option key={variable.id} value={variable.id}>
                    {variable.key} ({variable.scopeLabel ?? variable.scope_type}
                    )
                    {variables.some(
                      (other) =>
                        other.id !== variable.id &&
                        unicodeCaseFold(other.key) ===
                          unicodeCaseFold(variable.key) &&
                        (other.scopeLabel ?? other.scope_type) ===
                          (variable.scopeLabel ?? variable.scope_type),
                    )
                      ? ` · ${variable.id}`
                      : ""}
                  </option>
                ))}
              </Select>
              <Inline justify="end" gap={2}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsOpen(false);
                    anchorRef.current?.focus();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!selected}
                  onClick={() => {
                    if (!selected) return;
                    setIsOpen(false);
                    onInsert(selected);
                  }}
                >
                  Insert
                </Button>
              </Inline>
            </>
          )}
        </Stack>
      </Popover>
    </Inline>
  );
}

export function insertVariableToken(
  value: string,
  token: string,
  selectionStart = value.length,
  selectionEnd = selectionStart,
) {
  const insertion = `{${token}}`;
  return {
    value:
      value.slice(0, selectionStart) + insertion + value.slice(selectionEnd),
    cursor: selectionStart + insertion.length,
  };
}
